import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import CommandPalette from "./components/shared/CommandPalette";
import { AppSyncProvider } from "./contexts/AppSyncContext";
import { IntegrationModeProvider } from "./contexts/IntegrationModeContext";
import { AuthProvider } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { AppModeProvider } from "./contexts/AppModeContext";
import { SpecialtyProvider } from "./contexts/SpecialtyContext";

// Landing Page
import LandingPage from "./pages/LandingPage";
import OnboardingPage from "./pages/OnboardingPage";

// Public pages (no auth)
import PublicBookingPage from "./pages/public/PublicBookingPage";
import ShortBookingRedirect from "./pages/public/ShortBookingRedirect";

// Auth Pages
import AuthPage from "./pages/auth/AuthPage";
import MFAPage from "./pages/auth/MFAPage";
import AppSelectorPage from "./pages/auth/AppSelectorPage";
import SetupWizardPage from "./pages/auth/SetupWizardPage";
import SetupSuccessPage from "./pages/auth/SetupSuccessPage";
import ShowMePage from "./pages/showme/ShowMePage";

// revQ Pages
import RevQDashboard from "./pages/revq/RevQDashboard";
import RevQEncounters from "./pages/revq/RevQEncounters";
import RevQAIClaims from "./pages/revq/RevQAIClaims";
import RevQAIClaimsWizard from "./pages/revq/RevQAIClaimsWizard";
import RevQBillingFocus from "./pages/revq/RevQBillingFocus";
import BatchScrubDashboard from "./pages/revq/BatchScrubDashboard";
import RevQClaims from "./pages/revq/RevQClaims";
import RevQDenials from "./pages/revq/RevQDenials";
import RevQPayments from "./pages/revq/RevQPayments";
import RevQPatientBilling from "./pages/revq/RevQPatientBilling";
import RevQAnalytics from "./pages/revq/RevQAnalytics";
import RevQAIRecommendations from "./pages/revq/RevQAIRecommendations";
import RevQPayersRules from "./pages/revq/RevQPayersRules";
import RevQIntegrations from "./pages/revq/RevQIntegrations";
import RevQSettings from "./pages/revq/RevQSettings";
import RevQPatientChart from "./pages/revq/RevQPatientChart";
import RevQTeamSettings from "./pages/revq/RevQTeamSettings";
import RevQSupervisorDashboard from "./pages/revq/RevQSupervisorDashboard";
import CoderView from "./pages/revq/CoderView";
import DenialsView from "./pages/revq/DenialsView";
import RevQLayout from "./components/layout/RevQLayout";

// Lumina Pages
import LuminaDashboard from "./pages/lumina/LuminaDashboard";
import LuminaNewEncounter from "./pages/lumina/LuminaNewEncounter";
import LuminaAmbient from "./pages/lumina/LuminaAmbient";
import LuminaDictation from "./pages/lumina/LuminaDictation";
import LuminaLens from "./pages/lumina/LuminaLens";
import LuminaSOAPNotes from "./pages/lumina/LuminaSOAPNotes";
import LuminaDoctorMode from "./pages/lumina/LuminaDoctorMode";
import LuminaCoding from "./pages/lumina/LuminaCoding";
import LuminaDocumentation from "./pages/lumina/LuminaDocumentation";
import LuminaAppointments from "./pages/lumina/LuminaAppointments";
// LuminaPatientProfile deprecated — patients/:id now redirects to clinical-focus
const PatientRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/lumina/clinical-focus?patientId=${id}`} replace />;
};

import LuminaIntegrations from "./pages/lumina/LuminaIntegrations";
import LuminaCredits from "./pages/lumina/LuminaCredits";
import LuminaPatients from "./pages/lumina/LuminaPatients";
import LuminaSettings from "./pages/lumina/LuminaSettings";
import LuminaMyDocuments from "./pages/lumina/LuminaMyDocuments";
import LuminaHelp from "./pages/lumina/LuminaHelp";
import LuminaAIInsights from "./pages/lumina/LuminaAIInsights";
import LuminaMIPS from "./pages/lumina/LuminaMIPS";
import LuminaMADashboard from "./pages/lumina/LuminaMADashboard";
import HandoffNotesPage from "./pages/lumina/HandoffNotesPage";
import PreVisitPrepPage from "./pages/lumina/PreVisitPrepPage";
import LuminaLayout from "./components/layout/LuminaLayout";
import FhirSubscriptionsDashboard from "./pages/FhirSubscriptionsDashboard";
import TenantAdmin from "./pages/TenantAdmin";
import PlatformAdminPage, { ImpersonationBanner } from "./pages/PlatformAdmin";
import { TenantProvider, useTenant } from "./contexts/TenantContext";

// Echo Pages
import EchoDashboard from "./pages/echo/EchoDashboard";
import EchoLens from "./pages/echo/EchoLens";
import EchoSchedule from "./pages/echo/EchoSchedule";
import EchoPatients from "./pages/echo/EchoPatients";
import EchoPatientChart from "./pages/echo/EchoPatientChart";
import EchoAIAgents from "./pages/echo/EchoAIAgents";
import EchoComms from "./pages/echo/EchoComms";
import EchoAnalytics from "./pages/echo/EchoAnalytics";
import EchoBilling from "./pages/echo/EchoBilling";
import EchoSettings from "./pages/echo/EchoSettings";
import EchoIntegrations from "./pages/echo/EchoIntegrations";
import EchoFrontDesk from "./pages/echo/EchoFrontDesk";
import EchoScheduler from "./pages/echo/EchoScheduler";
import SchedulerTraceDebug from "./pages/echo/SchedulerTraceDebug";
import SchedulerRoute from "./pages/echo/SchedulerRoute";
import EchoPatientHub from "./pages/echo/EchoPatientHub";
import EchoLayout from "./components/layout/EchoLayout";
// Legacy /lumina/team route now redirects to /lumina/settings?tab=teams;
// the LuminaTeam page component is intentionally no longer imported.

function GlobalImpersonationBanner() {
  const { isImpersonating, activeTenant } = useTenant();
  if (!isImpersonating || !activeTenant) return null;
  return (
    <div className="fixed top-14 inset-x-0 z-[55] px-4">
      <div className="max-w-7xl mx-auto mt-2">
        <ImpersonationBanner tenant={activeTenant} />
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
      <AppModeProvider>
      <SpecialtyProvider>
      <NotificationProvider>
      <IntegrationModeProvider>
      <AppSyncProvider>
      <TenantProvider>
      <GlobalImpersonationBanner />
      <Routes>
        {/* Landing Page */}
        <Route path="/" element={<AuthPage />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        {/* Public patient-facing booking link (no auth) */}
        <Route path="/book/:slug" element={<PublicBookingPage />} />
        {/* Mini URL — 6-char short code resolves to /book/:slug */}
        <Route path="/s/:code" element={<ShortBookingRedirect />} />
        
        {/* Auth Flow */}
        <Route path="/signup" element={<AuthPage />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/apidemo/login" element={<AuthPage />} />
        <Route path="/revqdemo/login" element={<AuthPage />} />
        <Route path="/showme" element={<ShowMePage />} />
        {/* Stripe's hardcoded return URL after embedded-checkout setup completion.
            The vendor configures `return_url` as `{origin}/edi/login?setup_session_id=…`.
            We mount AuthPage here too so the redirect lands on a real route and
            AuthPage's useEffect can call signup/finalize from the session id. */}
        <Route path="/edi/login" element={<AuthPage />} />
        <Route path="/mfa" element={<MFAPage />} />
        <Route path="/app-selector" element={<AppSelectorPage />} />
        <Route path="/setup" element={<SetupWizardPage />} />
        <Route path="/setup-success" element={<SetupSuccessPage />} />
        
        {/* revQ Billing Focus (standalone — no sidebar) */}
        <Route path="/revq/billing-focus" element={<RevQBillingFocus />} />
        <Route path="/revq/batch-scrub" element={<BatchScrubDashboard />} />

        {/* Patient Hub (standalone — public, no auth needed) */}
        <Route path="/patient-hub/checkin" element={<EchoPatientHub />} />

        {/* revQ Routes */}
        <Route path="/revq" element={<RevQLayout />}>
          <Route index element={<RevQDashboard />} />
          <Route path="encounters" element={<RevQEncounters />} />
          <Route path="ai-claims" element={<RevQAIClaims />} />
          <Route path="ai-claims/wizard" element={<RevQAIClaimsWizard />} />
          <Route path="claims" element={<RevQClaims />} />
          <Route path="denials" element={<RevQDenials />} />
          <Route path="payments" element={<RevQPayments />} />
          <Route path="patient-billing" element={<RevQPatientBilling />} />
          <Route path="analytics" element={<RevQAnalytics />} />
          <Route path="ai-recommendations" element={<RevQAIRecommendations />} />
          <Route path="payers-rules" element={<RevQPayersRules />} />
          <Route path="integrations" element={<RevQIntegrations />} />
          <Route path="patients/:id" element={<RevQPatientChart />} />
          <Route path="team" element={<RevQTeamSettings />} />
          <Route path="supervisor" element={<RevQSupervisorDashboard />} />
          <Route path="coder" element={<CoderView />} />
          <Route path="denials-focus" element={<DenialsView />} />
          <Route path="settings" element={<RevQSettings />} />
          {/* Scheduler renders inside RevQ layout when triggered from RevQ sidebar */}
          <Route path="scheduler" element={<SchedulerRoute />} />
        </Route>
        
        {/* Lumina Routes */}
        <Route path="/lumina" element={<LuminaLayout />}>
          <Route index element={<LuminaDashboard />} />
          <Route path="appointments" element={<LuminaAppointments />} />
          <Route path="new-encounter" element={<LuminaNewEncounter />} />
          <Route path="ambient" element={<LuminaAmbient />} />
          <Route path="dictation" element={<LuminaDictation />} />
          <Route path="lens" element={<LuminaLens />} />
          <Route path="capture" element={<Navigate to="/lumina/documentation" replace />} />
          <Route path="documentation" element={<LuminaDocumentation />} />
          <Route path="soap-notes" element={<LuminaSOAPNotes />} />
          <Route path="soap-notes/generate" element={<LuminaSOAPNotes />} />
          <Route path="coding" element={<LuminaCoding />} />
          <Route path="patients" element={<LuminaPatients />} />
          {/* patients/:id redirects to unified clinical workspace */}
          <Route path="patients/:id" element={<PatientRedirect />} />
          <Route path="integrations" element={<LuminaIntegrations />} />
          <Route path="credits" element={<LuminaCredits />} />
          <Route path="ai-insights" element={<LuminaAIInsights />} />
          <Route path="mips" element={<LuminaMIPS />} />
          <Route path="settings" element={<LuminaSettings />} />
          <Route path="my-documents" element={<LuminaMyDocuments />} />
          <Route path="ma" element={<LuminaMADashboard />} />
          <Route path="ma/handoff" element={<HandoffNotesPage />} />
          <Route path="ma/prep" element={<PreVisitPrepPage />} />
          <Route path="ma/forms" element={<PreVisitPrepPage />} />
          <Route path="ma/sessions" element={<LuminaDocumentation />} />
          <Route path="help" element={<LuminaHelp />} />
          {/* ── Team + Scheduler relocated from Echo (IA change; functionality unchanged) ── */}
          {/* ── Scheduler: route-level split by mode (demo → legacy Echo UX;
                real → API-wired EchoScheduler) ── */}
          <Route path="scheduler" element={<SchedulerRoute />} />
          {/* FHIR R5 topic-based Subscription health dashboard (P3) */}
          <Route path="integrations/fhir-subscriptions" element={<FhirSubscriptionsDashboard />} />
          <Route path="tenants/admin" element={<TenantAdmin />} />
          <Route path="admin/platform" element={<PlatformAdminPage />} />
          {/* Legacy Team & Roles sidebar link — now consolidated into Settings → Teams tab */}
          <Route path="team" element={<Navigate to="/lumina/settings?tab=teams" replace />} />
        </Route>
        
        {/* Lumina Clinical Focus (standalone, no sidebar) */}
        <Route path="/lumina/clinical-focus" element={<LuminaDoctorMode />} />
        {/* Legacy redirect */}
        <Route path="/lumina/doctor-mode" element={<Navigate to="/lumina/clinical-focus" replace />} />
        
        {/* Echo Front Desk (standalone, no sidebar) */}
        <Route path="/echo/front-desk" element={<EchoFrontDesk />} />

        {/* Echo Routes */}
        <Route path="/echo" element={<EchoLayout />}>
          <Route index element={<EchoDashboard />} />
          <Route path="lens" element={<EchoLens />} />
          <Route path="agents" element={<EchoAIAgents />} />
          <Route path="schedule" element={<EchoSchedule />} />
          {/* Scheduler renders inside Echo layout when triggered from Echo sidebar */}
          <Route path="scheduler" element={<SchedulerRoute />} />
          {/* Internal ops debug — admin-gated. NO PHI on this page. */}
          <Route path="scheduler/_trace" element={<SchedulerTraceDebug />} />
          <Route path="patients" element={<EchoPatients />} />
          <Route path="patients/:id" element={<EchoPatientChart />} />
          <Route path="comms" element={<EchoComms />} />
          <Route path="analytics" element={<EchoAnalytics />} />
          <Route path="billing" element={<EchoBilling />} />
          <Route path="integrations" element={<EchoIntegrations />} />
          <Route path="settings" element={<EchoSettings />} />
        </Route>
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CommandPalette />
      <Toaster position="top-right" />
      </TenantProvider>
      </AppSyncProvider>
      </IntegrationModeProvider>
      </NotificationProvider>
      </SpecialtyProvider>
      </AppModeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
